import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Send, Eye, Users, Plus, Trash2, Search, X, Upload, Type,
  Image as ImageIcon, MousePointerClick, Minus, Mail, RefreshCw,
  Shuffle, Repeat, Play, Pause, Clock, Zap, Pencil,
} from "lucide-react";
import { api } from "@/lib/api";

const BLANK_CAMPAIGN = {
  subject: "",
  preheader: "",
  hero_image_url: "",
  background_image_url: "",
  blocks: [
    { type: "heading", text: "New from Pulse Media", align: "left" },
    { type: "text",    text: "Something big just dropped. Tap the button below to check it out.", align: "left" },
    { type: "button",  text: "Listen Now", url: "https://pulsemedia.vip", align: "center" },
  ],
  footer_note: "You're receiving this because you opted in via Pulse Media.",
  audience: "all",
  test_emails: [""],
  randomize_count: 0,
  randomize_source: "all",   // "all" (full list) | "audience" (filtered)
  text_color: "",
  button_color: "",
  button_text_color: "",
};

const DAYS = [
  { v: 0, label: "Mon" },
  { v: 1, label: "Tue" },
  { v: 2, label: "Wed" },
  { v: 3, label: "Thu" },
  { v: 4, label: "Fri" },
  { v: 5, label: "Sat" },
  { v: 6, label: "Sun" },
];

/** Coerce ANY error response into a plain string — FastAPI validation errors
 * are arrays of objects that will crash React if rendered raw. */
function errMsg(e, fallback = "Something went wrong.") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return e?.message || fallback;
}

export default function EmailAdmin() {
  const [tab, setTab] = useState("compose"); // compose | recurring | contacts | history
  return (
    <div data-testid="email-admin">
      <style>{`
        .admin-input {
          width: 100%; padding: 10px 12px; border-radius: 8px;
          background: #0a0a0a; border: 1px solid rgba(255,255,255,0.1);
          color: #fff; font-size: 14px; outline: none;
          transition: border-color 0.15s;
        }
        .admin-input:focus { border-color: rgba(0,184,255,0.5); }
      `}</style>
      <div className="label-mono mb-2">/ Outreach</div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Email Blasts</h1>
          <p className="text-zinc-500 text-sm mt-2">Compose branded email announcements & manage your master contact list.</p>
        </div>
        <div className="flex gap-1 p-1 rounded-lg bg-[#121215] border border-white/5">
          {[
            { k: "compose",   label: "Compose",   icon: Send },
            { k: "recurring", label: "Recurring", icon: Repeat },
            { k: "contacts",  label: "Contacts",  icon: Users },
            { k: "history",   label: "History",   icon: Mail },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold tracking-widest transition ${tab === t.k ? "bg-cyan-500/15 text-cyan-300" : "text-zinc-500 hover:text-zinc-300"}`}
              data-testid={`email-tab-${t.k}`}
            >
              <t.icon size={12}/> {t.label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {tab === "compose" && <Compose/>}
      {tab === "recurring" && <Recurring/>}
      {tab === "contacts" && <Contacts/>}
      {tab === "history" && <History/>}
    </div>
  );
}

/* ---------------- Compose ---------------- */
function Compose() {
  const [c, setC] = useState(BLANK_CAMPAIGN);
  const [previewHtml, setPreviewHtml] = useState("");
  const [recipientCount, setRecipientCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showPickContacts, setShowPickContacts] = useState(false);
  const [sendProgress, setSendProgress] = useState(null); // { id, total, sent, failed, status }

  const setBlock = (i, patch) => {
    setC((cur) => ({ ...cur, blocks: cur.blocks.map((b, idx) => idx === i ? { ...b, ...patch } : b) }));
  };
  const removeBlock = (i) => setC((cur) => ({ ...cur, blocks: cur.blocks.filter((_, idx) => idx !== i) }));
  const moveBlock = (i, dir) => setC((cur) => {
    const arr = [...cur.blocks];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return cur;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...cur, blocks: arr };
  });
  const addBlock = (type) => setC((cur) => ({
    ...cur,
    blocks: [...cur.blocks, {
      _id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `b_${Date.now()}_${Math.random()}`,
      type,
      text: type === "heading" ? "New heading" : type === "text" ? "Add your paragraph here." : type === "button" ? "Click here" : "",
      url: type === "button" ? "https://pulsemedia.vip" : "",
      image_url: "",
      align: type === "button" ? "center" : "left",
    }],
  }));

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/admin/email-campaigns/preview", c);
      setPreviewHtml(r.data.html);
      setRecipientCount(r.data.recipients);
    } catch (e) {
      toast.error(errMsg(e, "Preview failed."));
    } finally {
      setPreviewing(false);
    }
  };

  const doSend = async () => {
    if (!c.subject.trim()) { toast.error("Subject is required."); return; }
    const target = c.audience === "test" ? (c.test_emails || []).filter(Boolean) : null;
    let confirmMsg;
    if (c.audience === "test") confirmMsg = `Send TEST email to ${target?.length || 0} address(es)?`;
    else if (c.audience === "custom") confirmMsg = `Send this blast to ${(c.to_emails || []).length} selected contact(s)?\n\nThis cannot be undone.`;
    else confirmMsg = `Send this blast to ${recipientCount ?? "ALL"} contacts?\n\nThis cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/email-campaigns", c);
      const cid = r.data.campaign_id;
      toast.success(`Send started · ${r.data.total} recipient${r.data.total === 1 ? "" : "s"}`);
      setSendProgress({ id: cid, total: r.data.total, sent: 0, failed: 0, status: "sending" });
      // Poll progress
      const iv = setInterval(async () => {
        try {
          const p = await api.get(`/admin/email-campaigns/${cid}`);
          setSendProgress({ id: cid, total: p.data.recipient_count, sent: p.data.sent, failed: p.data.failed, status: p.data.status });
          if (p.data.status === "sent") {
            clearInterval(iv);
            toast.success(`Done! Sent ${p.data.sent} · Failed ${p.data.failed}`);
            setSending(false);
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error(errMsg(e, "Send failed."));
      setSending(false);
    }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/email-list/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setC((cur) => ({ ...cur, hero_image_url: r.data.url }));
      toast.success("Hero image uploaded.");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed."));
    }
  };

  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-6">
      {/* Editor */}
      <div className="space-y-4">
        <div className="p-5 rounded-2xl bg-[#121215] border border-white/5">
          <Label>Subject line</Label>
          <input value={c.subject} onChange={(e) => setC({ ...c, subject: e.target.value })}
                 placeholder="e.g. CSN by KeeZY — out now."
                 className="admin-input" data-testid="email-subject"/>
          <Label className="mt-4">Preheader (preview text)</Label>
          <input value={c.preheader} onChange={(e) => setC({ ...c, preheader: e.target.value })}
                 placeholder="Preview snippet shown in the inbox next to the subject"
                 className="admin-input"/>
          <Label className="mt-4">Hero image (optional)</Label>
          <div className="flex gap-2">
            <input value={c.hero_image_url} onChange={(e) => setC({ ...c, hero_image_url: e.target.value })}
                   placeholder="Paste URL or upload →"
                   className="admin-input"/>
            <label className="px-3 py-2 rounded-lg border border-white/15 hover:border-cyan-400/60 text-cyan-300 text-xs font-bold tracking-widest cursor-pointer inline-flex items-center gap-1.5 whitespace-nowrap">
              <Upload size={12}/> UPLOAD
              <input type="file" accept="image/*" className="hidden"
                     onChange={(e) => uploadHero(e.target.files?.[0])}
                     data-testid="email-hero-upload"/>
            </label>
          </div>
          <Label className="mt-4">Background image URL (50% opacity, optional)</Label>
          <input value={c.background_image_url || ""} onChange={(e) => setC({ ...c, background_image_url: e.target.value })}
                 placeholder="https://…/background.jpg"
                 className="admin-input" data-testid="email-bg-image"/>
          <div className="text-[10px] text-zinc-500 mt-1.5">
            Sits behind the email module with a dark overlay (~50% dim). Leave blank for solid black.
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-[#121215] border border-white/5">
          <div className="flex items-center justify-between mb-3">
            <Label className="!mb-0">Content blocks</Label>
            <div className="flex gap-1">
              <IconBtn title="Heading"  onClick={() => addBlock("heading")}><Type size={12}/></IconBtn>
              <IconBtn title="Text"     onClick={() => addBlock("text")}>P</IconBtn>
              <IconBtn title="Image"    onClick={() => addBlock("image")}><ImageIcon size={12}/></IconBtn>
              <IconBtn title="Button"   onClick={() => addBlock("button")}><MousePointerClick size={12}/></IconBtn>
              <IconBtn title="Divider"  onClick={() => addBlock("divider")}><Minus size={12}/></IconBtn>
            </div>
          </div>
          <div className="space-y-3">
            {c.blocks.map((b, i) => (
              <BlockEditor key={b._id || `b-${i}`} block={b} idx={i}
                           onChange={(patch) => setBlock(i, patch)}
                           onRemove={() => removeBlock(i)}
                           onMove={(dir) => moveBlock(i, dir)}/>
            ))}
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-[#121215] border border-white/5">
          <Label>Global colors (defaults)</Label>
          <div className="grid grid-cols-3 gap-3">
            <ColorField label="TEXT" value={c.text_color} onChange={(v) => setC({ ...c, text_color: v })} fallback="#a0a0a3" testid="email-global-text-color"/>
            <ColorField label="BUTTON" value={c.button_color} onChange={(v) => setC({ ...c, button_color: v })} fallback="#00b8ff" testid="email-global-button-color"/>
            <ColorField label="BTN TEXT" value={c.button_text_color} onChange={(v) => setC({ ...c, button_text_color: v })} fallback="#000000" testid="email-global-button-text-color"/>
          </div>
          <div className="text-[10px] text-zinc-500 mt-2">
            Applied to any block that doesn&apos;t override its own color.
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-[#121215] border border-white/5">
          <Label>Footer note (small print)</Label>
          <input value={c.footer_note} onChange={(e) => setC({ ...c, footer_note: e.target.value })}
                 className="admin-input"/>
        </div>

        <div className="p-5 rounded-2xl bg-[#121215] border border-white/5">
          <Label>Recipients</Label>
          <div className="flex gap-1 p-1 rounded-md bg-black/40 mb-3">
            {[
              { v: "all",    label: "Entire List" },
              { v: "custom", label: "Choose"      },
              { v: "test",   label: "Test send"   },
            ].map((o) => (
              <button key={o.v} onClick={() => setC({ ...c, audience: o.v })}
                      className={`flex-1 px-3 py-2 rounded text-xs font-bold tracking-widest transition ${c.audience === o.v ? "bg-cyan-500/15 text-cyan-300" : "text-zinc-500 hover:text-zinc-300"}`}
                      data-testid={`email-audience-${o.v}`}>
                {o.label.toUpperCase()}
              </button>
            ))}
          </div>
          {c.audience === "test" && (
            <div>
              <div className="text-[10px] text-zinc-500 mb-1">Test email(s), comma-separated:</div>
              <input value={(c.test_emails || []).join(", ")}
                     onChange={(e) => setC({ ...c, test_emails: e.target.value.split(",").map((s) => s.trim()) })}
                     placeholder="you@example.com"
                     className="admin-input" data-testid="email-test-addrs"/>
            </div>
          )}
          {c.audience === "custom" && (
            <div>
              <button onClick={() => setShowPickContacts(true)}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-white/15 hover:border-cyan-400/60 text-cyan-300 text-xs font-black tracking-widest"
                      data-testid="email-pick-contacts-btn">
                <Users size={12}/> PICK CONTACTS ({(c.to_emails || []).length} SELECTED)
              </button>
            </div>
          )}

          {/* Randomize block */}
          <div className="mt-4 pt-4 border-t border-white/5">
            <div className="flex items-center justify-between mb-2">
              <Label className="!mb-0 inline-flex items-center gap-1.5"><Shuffle size={10}/> Randomize (optional)</Label>
              {c.randomize_count > 0 && (
                <button onClick={() => setC({ ...c, randomize_count: 0 })}
                        className="text-[10px] text-red-400/70 hover:text-red-300 tracking-widest font-black">CLEAR</button>
              )}
            </div>
            <div className="flex gap-2">
              <input type="number" min="0" step="1"
                     value={c.randomize_count || 0}
                     onChange={(e) => setC({ ...c, randomize_count: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                     placeholder="0 = no random pick"
                     className="admin-input flex-1"
                     data-testid="email-randomize-count"/>
              <select value={c.randomize_source || "all"}
                      onChange={(e) => setC({ ...c, randomize_source: e.target.value })}
                      className="admin-input !w-auto"
                      data-testid="email-randomize-source">
                <option value="all">From entire list</option>
                <option value="audience">From selected audience</option>
              </select>
            </div>
            <div className="text-[10px] text-zinc-500 mt-1.5">
              Draw N random contacts. Leave 0 to send to everyone in the audience.
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={doPreview} disabled={previewing}
                  className="flex-1 px-5 py-3 rounded-full border-2 border-white/20 hover:border-white/50 text-xs font-black tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="email-preview-btn">
            {previewing ? <RefreshCw size={12} className="animate-spin"/> : <Eye size={12}/>} PREVIEW
          </button>
          <button onClick={doSend} disabled={sending}
                  className="flex-1 px-5 py-3 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50 hover:scale-[1.02] transition-transform"
                  data-testid="email-send-btn">
            {sending ? <RefreshCw size={12} className="animate-spin"/> : <Send size={12}/>} SEND BLAST
          </button>
        </div>
        {recipientCount !== null && !sendProgress && (
          <div className="text-center text-zinc-500 text-[11px] tracking-widest">
            {recipientCount.toLocaleString()} RECIPIENT{recipientCount === 1 ? "" : "S"}
          </div>
        )}
        {sendProgress && (
          <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/25" data-testid="email-send-progress">
            <div className="text-[10px] tracking-widest text-emerald-300 mb-2">/ {sendProgress.status.toUpperCase()}</div>
            <div className="text-sm text-white font-mono">
              <span className="text-emerald-300">{sendProgress.sent}</span> sent ·
              <span className="text-red-300 ml-1">{sendProgress.failed}</span> failed ·
              <span className="text-zinc-400 ml-1">{sendProgress.total} total</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-emerald-400 transition-all"
                   style={{ width: `${Math.round(((sendProgress.sent + sendProgress.failed) / Math.max(1, sendProgress.total)) * 100)}%` }}/>
            </div>
            <div className="text-zinc-500 text-[10px] mt-2">Sending at ~2/sec — you can close this tab; sending continues on the server.</div>
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="rounded-2xl bg-[#0e0e10] border border-white/5 overflow-hidden sticky top-4 h-fit">
        <div className="p-3 border-b border-white/5 flex items-center justify-between">
          <div className="label-mono">/ live preview</div>
          <div className="text-zinc-500 text-[10px]">Renders exactly as recipients will see it</div>
        </div>
        <div className="min-h-[80vh]" style={{ background: "#0a0a0a" }}>
          {previewHtml ? (
            <iframe title="Email preview" srcDoc={previewHtml} className="w-full min-h-[80vh] border-0" data-testid="email-preview-iframe"/>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-zinc-500 text-sm">
              <Eye size={24} className="mb-3 opacity-40"/>
              Tap PREVIEW to render your email.
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showPickContacts && (
          <PickContactsModal
            selected={c.to_emails || []}
            onClose={() => setShowPickContacts(false)}
            onDone={(emails) => setC({ ...c, to_emails: emails })}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------------- Pick Contacts ---------------- */
function PickContactsModal({ selected, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [chosen, setChosen] = useState(new Set(selected || []));

  const load = async (query = "") => {
    const r = await api.get("/admin/email-list", { params: { q: query, limit: 2000 } });
    setRows(r.data.rows);
  };
  useEffect(() => { load(); }, []);

  const toggle = (email) => setChosen((cur) => {
    const next = new Set(cur);
    if (next.has(email)) next.delete(email); else next.add(email);
    return next;
  });
  const selectAllVisible = () => {
    const emails = (rows || []).map((r) => r.email);
    setChosen((cur) => new Set([...cur, ...emails]));
  };
  const deselectAllVisible = () => {
    const emails = new Set((rows || []).map((r) => r.email));
    setChosen((cur) => new Set([...cur].filter((e) => !emails.has(e))));
  };
  const clearAll = () => setChosen(new Set());

  const commit = () => {
    onDone(Array.from(chosen));
    onClose();
    toast.success(`${chosen.size} contact${chosen.size === 1 ? "" : "s"} selected.`);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }}
                  className="w-full max-w-3xl max-h-[85vh] rounded-2xl bg-[#121215] border border-white/10 flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="email-pick-modal">
        <div className="p-5 border-b border-white/5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-2xl font-bold tracking-tight">Pick Contacts</div>
            <div className="text-zinc-500 text-xs mt-1">{chosen.size.toLocaleString()} selected</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18}/></button>
        </div>
        <div className="p-5 border-b border-white/5 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
            <input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
                   placeholder="Filter by email or domain…"
                   className="w-full pl-9 pr-3 py-2 rounded-lg bg-black/50 border border-white/10 text-sm outline-none focus:border-cyan-500/40"
                   data-testid="email-pick-search"/>
          </div>
          <button onClick={selectAllVisible} className="px-3 py-2 rounded-md bg-cyan-500/15 text-cyan-300 text-[10px] font-black tracking-widest hover:bg-cyan-500/25" data-testid="email-pick-select-all">
            SELECT ALL VISIBLE
          </button>
          <button onClick={deselectAllVisible} className="px-3 py-2 rounded-md border border-white/15 text-zinc-400 text-[10px] font-black tracking-widest hover:border-white/40">
            DESELECT VISIBLE
          </button>
          <button onClick={clearAll} className="px-3 py-2 rounded-md border border-red-500/30 text-red-300 text-[10px] font-black tracking-widest hover:bg-red-500/10">
            CLEAR
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {!rows ? (
            <div className="p-8 text-center text-zinc-500 text-sm">Loading…</div>
          ) : (
            <ul>
              {rows.map((r) => {
                const on = chosen.has(r.email);
                return (
                  <li key={r.id}>
                    <button onClick={() => toggle(r.email)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition ${on ? "bg-cyan-500/10 hover:bg-cyan-500/15" : "hover:bg-white/[0.03]"}`}
                            data-testid={`email-pick-row-${r.email}`}>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-none ${on ? "bg-cyan-400 border-cyan-400" : "border-white/25"}`}>
                        {on && <span className="text-black text-[10px] font-black">✓</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{r.email}</div>
                        <div className="text-[10px] text-zinc-500 font-mono tracking-widest">{(r.source || "").toUpperCase()} · {(r.tags || []).join(", ")}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="p-5 border-t border-white/5 flex items-center justify-between gap-3">
          <div className="text-zinc-500 text-xs">{rows?.length || 0} shown · {chosen.size} selected</div>
          <button onClick={commit} className="px-5 py-2.5 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest hover:scale-[1.03] transition-transform" data-testid="email-pick-confirm">
            USE THESE {chosen.size} CONTACT{chosen.size === 1 ? "" : "S"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function BlockEditor({ block, onChange, onRemove, onMove }) {
  return (
    <div className="p-3 rounded-lg bg-black/40 border border-white/5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono tracking-widest text-cyan-300">{block.type.toUpperCase()}</div>
        <div className="flex gap-1">
          <button onClick={() => onMove(-1)} className="text-zinc-500 hover:text-white px-1" title="Move up">↑</button>
          <button onClick={() => onMove(1)}  className="text-zinc-500 hover:text-white px-1" title="Move down">↓</button>
          <button onClick={onRemove}          className="text-red-400/70 hover:text-red-300 px-1" title="Remove"><Trash2 size={12}/></button>
        </div>
      </div>
      {(block.type === "heading" || block.type === "text" || block.type === "button") && (
        <textarea rows={block.type === "text" ? 3 : 1}
                  value={block.text || ""}
                  onChange={(e) => onChange({ text: e.target.value })}
                  placeholder={block.type === "button" ? "Button label" : "Enter text…"}
                  className="admin-input !text-sm"/>
      )}
      {block.type === "image" && (
        <input value={block.image_url || ""} onChange={(e) => onChange({ image_url: e.target.value })}
               placeholder="https://…/image.jpg" className="admin-input"/>
      )}
      {(block.type === "button" || block.type === "image") && (
        <input value={block.url || ""} onChange={(e) => onChange({ url: e.target.value })}
               placeholder={block.type === "button" ? "Button link" : "(optional) link URL"}
               className="admin-input mt-2"/>
      )}
      {block.type !== "divider" && (
        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
          <div className="flex gap-1">
            {["left", "center", "right"].map((a) => (
              <button key={a} onClick={() => onChange({ align: a })}
                      className={`px-2 py-1 rounded text-[10px] font-bold tracking-widest ${block.align === a ? "bg-cyan-500/15 text-cyan-300" : "text-zinc-500 hover:text-zinc-300"}`}>
                {a.toUpperCase()}
              </button>
            ))}
          </div>
          {(block.type === "heading" || block.type === "text") && (
            <BlockColorSwatch label="Text" value={block.color}
                              onChange={(v) => onChange({ color: v })}
                              onClear={() => onChange({ color: "" })}/>
          )}
          {block.type === "button" && (
            <div className="flex gap-2 items-center">
              <BlockColorSwatch label="BG" value={block.color}
                                onChange={(v) => onChange({ color: v })}
                                onClear={() => onChange({ color: "" })}/>
              <BlockColorSwatch label="Text" value={block.text_color}
                                onChange={(v) => onChange({ text_color: v })}
                                onClear={() => onChange({ text_color: "" })}/>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BlockColorSwatch({ label, value, onChange, onClear }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[9px] text-zinc-500 font-mono tracking-widest">{label.toUpperCase()}</span>
      <label className="relative w-6 h-6 rounded-md border border-white/15 cursor-pointer overflow-hidden inline-block"
             style={{ background: value || "transparent", backgroundImage: !value ? "linear-gradient(135deg, #444 25%, transparent 25%, transparent 50%, #444 50%, #444 75%, transparent 75%, transparent)" : undefined, backgroundSize: !value ? "8px 8px" : undefined }}>
        <input type="color" value={value || "#00b8ff"} onChange={(e) => onChange(e.target.value)}
               className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"/>
      </label>
      {value && (
        <button onClick={onClear} className="text-[9px] text-zinc-500 hover:text-red-300" title="Clear (use global default)">×</button>
      )}
    </div>
  );
}

function ColorField({ label, value, onChange, fallback, testid }) {
  return (
    <div>
      <div className="text-[10px] text-zinc-500 mb-1 font-mono tracking-widest">{label}</div>
      <div className="flex items-center gap-2">
        <label className="relative w-9 h-9 rounded-md border border-white/15 cursor-pointer overflow-hidden inline-block flex-none"
               style={{ background: value || fallback }}>
          <input type="color" value={value || fallback} onChange={(e) => onChange(e.target.value)}
                 className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                 data-testid={testid}/>
        </label>
        <input type="text" value={value || ""}
               onChange={(e) => onChange(e.target.value)}
               placeholder={fallback}
               className="admin-input !py-1.5 !text-xs font-mono"/>
        {value && (
          <button onClick={() => onChange("")} className="text-[9px] text-zinc-500 hover:text-red-300 font-black tracking-widest">CLEAR</button>
        )}
      </div>
    </div>
  );
}

function Label({ children, className = "" }) {
  return <div className={`text-[10px] tracking-[0.2em] text-zinc-500 mb-2 font-mono ${className}`}>{children}</div>;
}
function IconBtn({ children, ...rest }) {
  return <button {...rest} className="w-7 h-7 rounded-md bg-black/50 border border-white/10 text-cyan-300 hover:bg-cyan-500/15 text-[10px] font-black inline-flex items-center justify-center">{children}</button>;
}

/* ---------------- Recurring ---------------- */
function freqLabel(rec) {
  const h = String(rec.hour).padStart(2, "0"), m = String(rec.minute).padStart(2, "0");
  const day = DAYS.find((d) => d.v === rec.day_of_week)?.label || "Mon";
  if (rec.frequency === "weekly")   return `Weekly · ${day} @ ${h}:${m}`;
  if (rec.frequency === "biweekly") return `Bi-weekly · ${day} @ ${h}:${m}`;
  if (rec.frequency === "monthly")  return `Monthly · day ${rec.day_of_month} @ ${h}:${m}`;
  return `${rec.frequency} @ ${h}:${m}`;
}

function Recurring() {
  const [rows, setRows] = useState(null);
  const [showEdit, setShowEdit] = useState(null); // null | {} | rec
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/email-recurring");
      setRows(r.data || []);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load recurring campaigns."));
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const togglePause = async (rec) => {
    setBusyId(rec.id);
    try {
      await api.patch(`/admin/email-recurring/${rec.id}`, { paused: !rec.paused });
      toast.success(rec.paused ? "Resumed." : "Paused.");
      load();
    } catch (e) { toast.error(errMsg(e, "Failed.")); }
    finally { setBusyId(null); }
  };
  const runNow = async (rec) => {
    if (!window.confirm(`Send "${rec.name}" now?`)) return;
    setBusyId(rec.id);
    try {
      const r = await api.post(`/admin/email-recurring/${rec.id}/run-now`);
      toast.success(`Send started · ${r.data.total} recipient${r.data.total === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed.")); }
    finally { setBusyId(null); }
  };
  const del = async (rec) => {
    if (!window.confirm(`Delete "${rec.name}"? This cannot be undone.`)) return;
    setBusyId(rec.id);
    try {
      await api.delete(`/admin/email-recurring/${rec.id}`);
      toast.success("Deleted.");
      load();
    } catch (e) { toast.error(errMsg(e, "Failed.")); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="email-recurring">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="text-zinc-500 text-sm">
          Recurring blasts run on a schedule (America/New_York). Each run re-selects recipients — perfect with{" "}
          <span className="text-cyan-300">Randomize</span> to draw a fresh N contacts each cycle.
        </div>
        <button onClick={() => setShowEdit({})}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest"
                data-testid="email-recurring-new-btn">
          <Plus size={12}/> NEW RECURRING
        </button>
      </div>

      <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
        {!rows ? (
          <div className="p-12 text-center text-zinc-500 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No recurring campaigns yet. Create one to schedule automated sends.</div>
        ) : (
          <ul>
            {rows.map((rec) => (
              <li key={rec.id} className="border-b border-white/5 last:border-b-0">
                <div className="p-5 flex flex-wrap items-center gap-4">
                  <div className={`w-2 h-2 rounded-full flex-none ${rec.paused ? "bg-zinc-500" : "bg-emerald-400 animate-pulse"}`}/>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg font-bold tracking-tight">{rec.name}</div>
                      {rec.paused && <span className="text-[9px] px-2 py-0.5 rounded-full bg-zinc-500/20 text-zinc-400 font-black tracking-widest">PAUSED</span>}
                      {(rec.campaign?.randomize_count || 0) > 0 && (
                        <span className="text-[9px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 font-black tracking-widest inline-flex items-center gap-1">
                          <Shuffle size={9}/> {rec.campaign.randomize_count} RANDOM
                        </span>
                      )}
                    </div>
                    <div className="text-zinc-500 text-xs mt-1 font-mono flex items-center gap-2 flex-wrap">
                      <Clock size={10}/> {freqLabel(rec)} · {rec.tz}
                      <span className="opacity-60">·</span>
                      <span>Runs: {rec.run_count || 0}</span>
                      {rec.last_run_at && <><span className="opacity-60">·</span><span>Last: {new Date(rec.last_run_at).toLocaleString()}</span></>}
                    </div>
                    <div className="text-zinc-600 text-[11px] mt-1 truncate">Subject: {rec.campaign?.subject}</div>
                    {rec.last_error && <div className="text-red-400 text-[11px] mt-1">Last error: {rec.last_error}</div>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => togglePause(rec)} disabled={busyId === rec.id}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[10px] font-black tracking-widest transition ${rec.paused ? "border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10" : "border-amber-400/40 text-amber-300 hover:bg-amber-500/10"}`}
                            data-testid={`email-recurring-toggle-${rec.id}`}>
                      {rec.paused ? <><Play size={10}/> RESUME</> : <><Pause size={10}/> PAUSE</>}
                    </button>
                    <button onClick={() => runNow(rec)} disabled={busyId === rec.id}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-cyan-400/40 text-cyan-300 hover:bg-cyan-500/10 text-[10px] font-black tracking-widest"
                            data-testid={`email-recurring-run-${rec.id}`}>
                      <Zap size={10}/> RUN NOW
                    </button>
                    <button onClick={() => setShowEdit(rec)} disabled={busyId === rec.id}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-zinc-300 hover:border-white/40 text-[10px] font-black tracking-widest"
                            data-testid={`email-recurring-edit-${rec.id}`}>
                      <Pencil size={10}/> EDIT
                    </button>
                    <button onClick={() => del(rec)} disabled={busyId === rec.id}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 text-[10px] font-black tracking-widest"
                            data-testid={`email-recurring-delete-${rec.id}`}>
                      <Trash2 size={10}/> DELETE
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AnimatePresence>
        {showEdit !== null && (
          <RecurringEditor
            rec={showEdit.id ? showEdit : null}
            onClose={() => setShowEdit(null)}
            onDone={() => { setShowEdit(null); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function RecurringEditor({ rec, onClose, onDone }) {
  const [name, setName] = useState(rec?.name || "");
  const [c, setC] = useState(() => ({ ...BLANK_CAMPAIGN, ...(rec?.campaign || {}) }));
  const [frequency, setFrequency] = useState(rec?.frequency || "weekly");
  const [dayOfWeek, setDayOfWeek] = useState(rec?.day_of_week ?? 1);
  const [dayOfMonth, setDayOfMonth] = useState(rec?.day_of_month ?? 1);
  const [hour, setHour] = useState(rec?.hour ?? 10);
  const [minute, setMinute] = useState(rec?.minute ?? 0);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  const doPreview = async () => {
    try {
      const r = await api.post("/admin/email-campaigns/preview", c);
      setPreviewHtml(r.data.html);
    } catch (e) { toast.error(errMsg(e, "Preview failed.")); }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/email-list/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setC((cur) => ({ ...cur, hero_image_url: r.data.url }));
      toast.success("Hero uploaded.");
    } catch (e) { toast.error(errMsg(e, "Upload failed.")); }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give this recurring campaign a name."); return; }
    if (!c.subject.trim()) { toast.error("Subject is required."); return; }
    setSaving(true);
    try {
      const body = {
        name, campaign: c, frequency,
        day_of_week: Number(dayOfWeek), day_of_month: Number(dayOfMonth),
        hour: Number(hour), minute: Number(minute),
        tz: "America/New_York",
        paused: rec?.paused || false,
      };
      if (rec?.id) {
        await api.patch(`/admin/email-recurring/${rec.id}`, body);
        toast.success("Updated.");
      } else {
        await api.post("/admin/email-recurring", body);
        toast.success("Recurring campaign created.");
      }
      onDone();
    } catch (e) { toast.error(errMsg(e, "Save failed.")); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }}
                  className="w-full max-w-5xl max-h-[92vh] rounded-2xl bg-[#121215] border border-white/10 flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="email-recurring-editor">
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="font-display text-2xl font-bold tracking-tight">
              {rec ? "Edit Recurring Blast" : "New Recurring Blast"}
            </div>
            <div className="text-zinc-500 text-xs mt-1">Runs automatically on the schedule below. Recipients are re-selected on every run.</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 grid lg:grid-cols-2 gap-5">
          {/* Left column: schedule + audience + basics */}
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-black/40 border border-white/5">
              <Label>Campaign name (internal only)</Label>
              <input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. Weekly New Music Digest"
                     className="admin-input" data-testid="email-recurring-name"/>
            </div>

            <div className="p-4 rounded-xl bg-black/40 border border-white/5">
              <Label>Schedule (America/New_York)</Label>
              <div className="flex gap-1 p-1 rounded-md bg-black/60 mb-3">
                {[
                  { v: "weekly",   label: "Weekly" },
                  { v: "biweekly", label: "Bi-weekly" },
                  { v: "monthly",  label: "Monthly" },
                ].map((f) => (
                  <button key={f.v} onClick={() => setFrequency(f.v)}
                          className={`flex-1 px-3 py-2 rounded text-xs font-bold tracking-widest transition ${frequency === f.v ? "bg-cyan-500/15 text-cyan-300" : "text-zinc-500 hover:text-zinc-300"}`}
                          data-testid={`email-recurring-freq-${f.v}`}>
                    {f.label.toUpperCase()}
                  </button>
                ))}
              </div>
              {(frequency === "weekly" || frequency === "biweekly") && (
                <div className="grid grid-cols-7 gap-1 mb-3">
                  {DAYS.map((d) => (
                    <button key={d.v} onClick={() => setDayOfWeek(d.v)}
                            className={`px-2 py-2 rounded text-[10px] font-black tracking-widest transition ${dayOfWeek === d.v ? "bg-cyan-400 text-black" : "bg-black/40 border border-white/10 text-zinc-400 hover:text-white"}`}>
                      {d.label.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
              {frequency === "monthly" && (
                <div className="mb-3">
                  <div className="text-[10px] text-zinc-500 mb-1">Day of month (1–28):</div>
                  <input type="number" min="1" max="28" value={dayOfMonth}
                         onChange={(e) => setDayOfMonth(Math.max(1, Math.min(28, parseInt(e.target.value || "1", 10))))}
                         className="admin-input"/>
                </div>
              )}
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <div className="text-[10px] text-zinc-500 mb-1">Hour (0–23)</div>
                  <input type="number" min="0" max="23" value={hour}
                         onChange={(e) => setHour(Math.max(0, Math.min(23, parseInt(e.target.value || "0", 10))))}
                         className="admin-input"/>
                </div>
                <div className="flex-1">
                  <div className="text-[10px] text-zinc-500 mb-1">Minute (0–59)</div>
                  <input type="number" min="0" max="59" value={minute}
                         onChange={(e) => setMinute(Math.max(0, Math.min(59, parseInt(e.target.value || "0", 10))))}
                         className="admin-input"/>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-black/40 border border-white/5">
              <Label>Subject line</Label>
              <input value={c.subject} onChange={(e) => setC({ ...c, subject: e.target.value })}
                     placeholder="e.g. Weekly Roundup — New Drops"
                     className="admin-input" data-testid="email-recurring-subject"/>
              <Label className="mt-3">Preheader</Label>
              <input value={c.preheader} onChange={(e) => setC({ ...c, preheader: e.target.value })}
                     className="admin-input"/>
              <Label className="mt-3">Hero image URL</Label>
              <div className="flex gap-2">
                <input value={c.hero_image_url} onChange={(e) => setC({ ...c, hero_image_url: e.target.value })}
                       placeholder="Paste URL or upload →" className="admin-input"/>
                <label className="px-3 py-2 rounded-lg border border-white/15 hover:border-cyan-400/60 text-cyan-300 text-xs font-bold tracking-widest cursor-pointer inline-flex items-center gap-1.5 whitespace-nowrap">
                  <Upload size={12}/> UPLOAD
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadHero(e.target.files?.[0])}/>
                </label>
              </div>
              <Label className="mt-3">Background image URL (50% opacity)</Label>
              <input value={c.background_image_url || ""} onChange={(e) => setC({ ...c, background_image_url: e.target.value })}
                     placeholder="https://…/background.jpg" className="admin-input"/>
            </div>

            <div className="p-4 rounded-xl bg-black/40 border border-white/5">
              <Label>Recipients + Randomize</Label>
              <div className="flex gap-1 p-1 rounded-md bg-black/60 mb-3">
                {[
                  { v: "all",    label: "Entire List" },
                  { v: "custom", label: "Custom"      },
                ].map((o) => (
                  <button key={o.v} onClick={() => setC({ ...c, audience: o.v })}
                          className={`flex-1 px-3 py-2 rounded text-xs font-bold tracking-widest transition ${c.audience === o.v ? "bg-cyan-500/15 text-cyan-300" : "text-zinc-500 hover:text-zinc-300"}`}>
                    {o.label.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="number" min="0" step="1"
                       value={c.randomize_count || 0}
                       onChange={(e) => setC({ ...c, randomize_count: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                       placeholder="0 = no random pick"
                       className="admin-input flex-1"
                       data-testid="email-recurring-randomize-count"/>
                <select value={c.randomize_source || "all"}
                        onChange={(e) => setC({ ...c, randomize_source: e.target.value })}
                        className="admin-input !w-auto">
                  <option value="all">From entire list</option>
                  <option value="audience">From selected audience</option>
                </select>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1.5">
                On every scheduled run, N contacts are re-sampled. Great for weekly digests to a fresh random slice.
              </div>
            </div>

            <div className="p-4 rounded-xl bg-black/40 border border-white/5">
              <div className="flex items-center justify-between mb-3">
                <Label className="!mb-0">Content blocks</Label>
                <div className="flex gap-1">
                  <IconBtn title="Heading" onClick={() => setC({ ...c, blocks: [...(c.blocks || []), { type: "heading", text: "New heading", align: "left" }] })}><Type size={12}/></IconBtn>
                  <IconBtn title="Text"    onClick={() => setC({ ...c, blocks: [...(c.blocks || []), { type: "text", text: "Add paragraph.", align: "left" }] })}>P</IconBtn>
                  <IconBtn title="Image"   onClick={() => setC({ ...c, blocks: [...(c.blocks || []), { type: "image", image_url: "", align: "left" }] })}><ImageIcon size={12}/></IconBtn>
                  <IconBtn title="Button"  onClick={() => setC({ ...c, blocks: [...(c.blocks || []), { type: "button", text: "Click here", url: "https://pulsemedia.vip", align: "center" }] })}><MousePointerClick size={12}/></IconBtn>
                  <IconBtn title="Divider" onClick={() => setC({ ...c, blocks: [...(c.blocks || []), { type: "divider" }] })}><Minus size={12}/></IconBtn>
                </div>
              </div>
              <div className="space-y-3">
                {(c.blocks || []).map((b, i) => (
                  <BlockEditor key={b._id || `b-${i}`} block={b} idx={i}
                               onChange={(patch) => setC({ ...c, blocks: c.blocks.map((bb, ii) => ii === i ? { ...bb, ...patch } : bb) })}
                               onRemove={() => setC({ ...c, blocks: c.blocks.filter((_, ii) => ii !== i) })}
                               onMove={(dir) => {
                                 const arr = [...c.blocks];
                                 const j = i + dir;
                                 if (j < 0 || j >= arr.length) return;
                                 [arr[i], arr[j]] = [arr[j], arr[i]];
                                 setC({ ...c, blocks: arr });
                               }}/>
                ))}
              </div>
            </div>
          </div>

          {/* Right column: live preview */}
          <div className="rounded-xl bg-[#0e0e10] border border-white/5 overflow-hidden sticky top-0 h-fit">
            <div className="p-3 border-b border-white/5 flex items-center justify-between">
              <div className="label-mono">/ preview</div>
              <button onClick={doPreview} className="text-[10px] font-black tracking-widest text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
                <Eye size={10}/> REFRESH
              </button>
            </div>
            {previewHtml ? (
              <iframe title="Preview" srcDoc={previewHtml} className="w-full min-h-[70vh] border-0"/>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-zinc-500 text-sm">
                <Eye size={20} className="mb-3 opacity-40"/>
                Tap REFRESH to render.
              </div>
            )}
          </div>
        </div>

        <div className="p-5 border-t border-white/5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-white/15 text-zinc-300 text-xs font-black tracking-widest hover:border-white/40">
            CANCEL
          </button>
          <button onClick={save} disabled={saving}
                  className="px-5 py-2.5 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="email-recurring-save">
            {saving ? <RefreshCw size={12} className="animate-spin"/> : <Repeat size={12}/>}
            {rec ? "SAVE CHANGES" : "CREATE RECURRING"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ---------------- Contacts ---------------- */
function Contacts() {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = async (query = "") => {
    const r = await api.get("/admin/email-list", { params: { q: query, limit: 500 } });
    setRows(r.data.rows); setTotal(r.data.total);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows || [], [rows]);

  const del = async (id, email) => {
    if (!window.confirm(`Remove ${email} from your list?`)) return;
    await api.delete(`/admin/email-list/${id}`);
    toast.success("Removed.");
    load(q);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
          <input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
                 placeholder="Search email…"
                 className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#121215] border border-white/10 text-sm focus:border-cyan-500/40 outline-none"/>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowImport(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/20 hover:border-white/50 text-xs font-black tracking-widest">
            <Upload size={12}/> IMPORT LIST
          </button>
          <button onClick={() => setShowAdd(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest">
            <Plus size={12}/> ADD CONTACT
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
        {!rows ? (
          <div className="p-12 text-center text-zinc-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No contacts.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] tracking-widest text-zinc-500 border-b border-white/5">
                  <th className="text-left px-5 py-3 font-mono">Email</th>
                  <th className="text-left px-5 py-3 font-mono">Source</th>
                  <th className="text-left px-5 py-3 font-mono">Tags</th>
                  <th className="text-left px-5 py-3 font-mono">Added</th>
                  <th className="text-right px-5 py-3 font-mono">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-3">{r.email}</td>
                    <td className="px-5 py-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 tracking-widest font-bold text-zinc-300">
                        {(r.source || "").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-zinc-500 text-xs">{(r.tags || []).join(", ")}</td>
                    <td className="px-5 py-3 text-zinc-500 text-xs font-mono">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => del(r.id, r.email)} className="text-red-400/70 hover:text-red-300">
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="text-zinc-500 text-xs mt-4 font-mono tracking-wider">
        {rows?.length || 0} SHOWN · {total.toLocaleString()} TOTAL
      </div>

      <AnimatePresence>
        {showAdd && <AddContactModal onClose={() => setShowAdd(false)} onDone={() => load(q)}/>}
        {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => load(q)}/>}
      </AnimatePresence>
    </div>
  );
}

function AddContactModal({ onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/admin/email-list", { emails: [email], source: "manual" });
      if (r.data.added) toast.success("Added.");
      else toast.info("Already in list.");
      onDone(); onClose();
    } catch (err) { toast.error(errMsg(err, "Failed.")); }
    finally { setSubmitting(false); }
  };
  return (
    <AdminModal onClose={onClose} title="Add Contact">
      <form onSubmit={submit} className="space-y-3">
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" className="admin-input" autoFocus/>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="admin-input"/>
        <button type="submit" disabled={submitting} className="w-full px-5 py-3 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest disabled:opacity-50">
          {submitting ? "ADDING…" : "ADD TO LIST"}
        </button>
      </form>
    </AdminModal>
  );
}

function ImportModal({ onClose, onDone }) {
  const [raw, setRaw] = useState("");
  const [file, setFile] = useState(null);
  const [source, setSource] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submitPaste = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const emails = raw.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
      const r = await api.post("/admin/email-list", { emails, source: source || "import" });
      toast.success(`Added ${r.data.added} · Skipped ${r.data.skipped}`);
      onDone(); onClose();
    } catch (err) { toast.error(errMsg(err, "Failed.")); }
    finally { setSubmitting(false); }
  };

  const submitFile = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file first."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", source || `file:${file.name}`);
      const r = await api.post("/admin/email-list/upload-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { source: source || `file:${file.name}` },
      });
      const d = r.data;
      if (d.warning) toast.warning(d.warning);
      else toast.success(`Added ${d.added} · Skipped ${d.skipped} · Found ${d.found_in_file} in file`);
      onDone(); onClose();
    } catch (err) { toast.error(errMsg(err, "Upload failed.")); }
    finally { setSubmitting(false); }
  };

  return (
    <AdminModal onClose={onClose} title="Import Emails" subtitle="Upload a CSV/XLSX/TXT file or paste addresses">
      <div className="space-y-5">
        {/* File upload */}
        <form onSubmit={submitFile} className="space-y-3 pb-5 border-b border-white/5">
          <div className="label-mono text-cyan-300">Upload file</div>
          <label className="block relative">
            <input type="file" accept=".csv,.xlsx,.xls,.txt"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   className="hidden" data-testid="email-import-file"/>
            <div className={`w-full px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer text-center transition ${file ? "border-cyan-400/60 bg-cyan-500/5" : "border-white/15 hover:border-cyan-400/40"}`}>
              {file ? (
                <>
                  <div className="text-cyan-300 text-sm font-semibold">{file.name}</div>
                  <div className="text-zinc-500 text-xs mt-1">{(file.size / 1024).toFixed(1)} KB · Click to change</div>
                </>
              ) : (
                <>
                  <div className="text-zinc-300 text-sm font-semibold">Choose CSV, XLSX, or TXT</div>
                  <div className="text-zinc-500 text-xs mt-1">Emails will be extracted automatically from any column</div>
                </>
              )}
            </div>
          </label>
          <input value={source} onChange={(e) => setSource(e.target.value)}
                 placeholder="Source tag (optional, e.g. 'la-small-biz-2026')"
                 className="admin-input !text-xs"/>
          <button type="submit" disabled={submitting || !file}
                  className="w-full px-5 py-3 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest disabled:opacity-50"
                  data-testid="email-import-file-submit">
            {submitting ? "UPLOADING…" : "IMPORT FROM FILE"}
          </button>
        </form>

        {/* Paste option */}
        <form onSubmit={submitPaste} className="space-y-3">
          <div className="label-mono">Or paste emails</div>
          <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={6}
                    placeholder="one@example.com&#10;two@example.com&#10;…"
                    className="admin-input font-mono !text-xs"/>
          <button type="submit" disabled={submitting || !raw.trim()}
                  className="w-full px-5 py-3 rounded-full border border-white/15 text-zinc-300 text-xs font-black tracking-widest disabled:opacity-50">
            {submitting ? "IMPORTING…" : "IMPORT PASTED"}
          </button>
        </form>
      </div>
    </AdminModal>
  );
}

/* ---------------- History ---------------- */
function History() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/email-campaigns").then((r) => setRows(r.data)); }, []);
  return (
    <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
      {!rows ? (
        <div className="p-12 text-center text-zinc-500 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-12 text-center text-zinc-500 text-sm">No campaigns sent yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-widest text-zinc-500 border-b border-white/5">
              <th className="text-left px-5 py-3 font-mono">Subject</th>
              <th className="text-left px-5 py-3 font-mono">Recipients</th>
              <th className="text-left px-5 py-3 font-mono">Sent</th>
              <th className="text-left px-5 py-3 font-mono">Failed</th>
              <th className="text-left px-5 py-3 font-mono">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-white/5">
                <td className="px-5 py-3">{c.subject}</td>
                <td className="px-5 py-3 text-zinc-400 font-mono text-xs">{c.recipient_count}</td>
                <td className="px-5 py-3 text-emerald-300 font-mono">{c.sent}</td>
                <td className="px-5 py-3 text-red-300 font-mono">{c.failed}</td>
                <td className="px-5 py-3 text-zinc-500 font-mono text-xs">{new Date(c.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ---------------- Modal shell ---------------- */
function AdminModal({ onClose, title, subtitle, children }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }}
                  className="w-full max-w-md rounded-2xl bg-[#121215] border border-white/10 p-6"
                  onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display text-2xl font-bold tracking-tight">{title}</div>
            {subtitle && <div className="text-zinc-500 text-xs mt-1">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18}/></button>
        </div>
        {children}
      </motion.div>
      <style>{`
        .admin-input {
          width: 100%; padding: 10px 12px; border-radius: 8px;
          background: #0a0a0a; border: 1px solid rgba(255,255,255,0.1);
          color: #fff; font-size: 14px; outline: none;
          transition: border-color 0.15s;
        }
        .admin-input:focus { border-color: rgba(0,184,255,0.5); }
      `}</style>
    </motion.div>
  );
}

// Global styles for admin inputs used outside the modal too
export const EmailAdminStyles = () => (
  <style>{`
    .admin-input {
      width: 100%; padding: 10px 12px; border-radius: 8px;
      background: #0a0a0a; border: 1px solid rgba(255,255,255,0.1);
      color: #fff; font-size: 14px; outline: none;
      transition: border-color 0.15s;
    }
    .admin-input:focus { border-color: rgba(0,184,255,0.5); }
  `}</style>
);
